import { Button, Callout, Dialog, LoadingRegion, Skeleton, useToast } from '@apovisio/ui';
import type { LivekitConnection, MeetingDetail } from '@apovisio/contracts';
import { X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { loadDevicePreferences } from '../../features/guest/device-preferences';
import { useMeeting } from '../../features/meetings/api';
import { WaitingRoomList, useWaitingRoom } from '../../features/meetings/WaitingRoomCard';
import { joinAsHost, leaveAsHost } from '../../features/room/api';
import { ConnectionLost, RoomView, disconnectOutcome } from '../../features/room/RoomView';
import { ApiClientError } from '../../lib/api';

/** Écran 8 côté hôte : salle de visio et salle d'attente dans le panneau latéral. */
export function HostRoomPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const meeting = useMeeting(id);
  const waitingRoom = useWaitingRoom(id);
  const [devices] = useState(loadDevicePreferences);
  const [connection, setConnection] = useState<LivekitConnection | null>(null);
  const [problem, setProblem] = useState<'lost' | 'ended' | 'error' | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [leaving, setLeaving] = useState<{ remoteIdentities: string[] } | null>(null);

  const join = useCallback(
    () =>
      joinAsHost(id).then(setConnection, (error: unknown) => {
        setErrorMessage(error instanceof ApiClientError ? error.message : 'Connexion impossible.');
        setProblem('error');
      }),
    [id],
  );

  useEffect(() => {
    void join();
  }, [join]);

  const ended = problem === 'ended' || waitingRoom.room?.meeting.status === 'ENDED';

  const rejoin = () => {
    setProblem(null);
    setConnection(null);
    void join();
  };

  if (problem === 'error' || ended) {
    return (
      <main className="app-centered">
        <Callout
          tone={ended ? 'info' : 'danger'}
          title={ended ? 'Cette visio est terminée.' : (errorMessage ?? 'Connexion impossible.')}
          live="alert"
        >
          <Link to={`/admin/reunions/${id}`}>Retour à la visio</Link>
        </Callout>
      </main>
    );
  }
  if (problem === 'lost') return <ConnectionLost onRejoin={rejoin} />;
  if (!connection || !meeting.data) {
    return (
      <main className="app-centered av-theme-room app-room-loading">
        <LoadingRegion label="Connexion à la salle">
          <Skeleton width={320} height={24} />
        </LoadingRegion>
      </main>
    );
  }

  const waitingCount = waitingRoom.room?.waiting.length ?? 0;
  return (
    <>
      <RoomView
        connection={connection}
        title={meeting.data.title}
        participantRole="host"
        chatEnabled={meeting.data.chatEnabled}
        screenShareEnabled={meeting.data.screenShareEnabled}
        devices={devices}
        sidePanel={{
          label: waitingCount > 0 ? `Salle d’attente (${String(waitingCount)})` : 'Salle d’attente',
          content: (close) => (
            <div className="av-stack">
              <div className="av-row av-row--between">
                <h2 className="app-room__panel-title">Salle d’attente</h2>
                <Button variant="ghost" icon={<X />} onClick={close}>
                  Fermer
                </Button>
              </div>
              <WaitingRoomList {...waitingRoom} />
            </div>
          ),
        }}
        onLeave={setLeaving}
        onDisconnected={(reason) => {
          const outcome = disconnectOutcome(reason);
          if (outcome === 'ended') setProblem('ended');
          else if (outcome === 'lost' || outcome === 'removed') setProblem('lost');
        }}
      />
      <LeaveDialog
        meeting={meeting.data}
        context={leaving}
        onCancel={() => {
          setLeaving(null);
        }}
        onDone={() => {
          void navigate(`/admin/reunions/${id}`);
        }}
      />
    </>
  );
}

/**
 * Départ de l'hôte (P-07, P-08) : le propriétaire voit qui reprend la visio, ou que la salle
 * sera fermée faute de co-hôte présent. « Terminer pour tous » est toujours proposé.
 */
function LeaveDialog({
  meeting,
  context,
  onCancel,
  onDone,
}: {
  meeting: MeetingDetail;
  context: { remoteIdentities: string[] } | null;
  onCancel: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [pending, setPending] = useState<'leave' | 'end' | null>(null);
  const owner = meeting.myRole === 'OWNER';
  const successor = meeting.coHosts.find((coHost) =>
    context?.remoteIdentities.includes(`h_${coHost.id}`),
  );

  const run = (endForAll: boolean) => {
    setPending(endForAll ? 'end' : 'leave');
    leaveAsHost(meeting.id, endForAll)
      .then(({ outcome }) => {
        if (outcome === 'transferred') {
          toast.show({
            tone: 'success',
            title: `${successor?.displayName ?? 'Un co-hôte'} est maintenant organisateur.`,
          });
        } else if (outcome === 'ended') {
          toast.show({ tone: 'success', title: 'Visio terminée pour tous.' });
        }
        onDone();
      })
      .catch((error: unknown) => {
        toast.show({
          tone: 'danger',
          title: 'Action impossible',
          description: error instanceof ApiClientError ? error.message : undefined,
        });
        setPending(null);
      });
  };

  const description = !owner
    ? 'Vous pourrez revenir tant que la visio est ouverte.'
    : successor
      ? `${successor.displayName} (co-hôte) deviendra organisateur. Vous pouvez aussi terminer la visio pour tout le monde.`
      : 'Aucun co-hôte n’est présent : quitter terminera la visio pour tous les participants.';

  return (
    <Dialog
      open={context !== null}
      onClose={onCancel}
      title="Quitter la visio ?"
      description={description}
      actions={
        <>
          <Button variant="secondary" onClick={onCancel} data-autofocus="">
            Rester
          </Button>
          <Button
            variant="secondary"
            loading={pending === 'leave'}
            disabled={pending !== null}
            onClick={() => {
              run(false);
            }}
          >
            Quitter
          </Button>
          <Button
            variant="danger"
            loading={pending === 'end'}
            disabled={pending !== null}
            onClick={() => {
              run(true);
            }}
          >
            Terminer pour tous
          </Button>
        </>
      }
    />
  );
}
